import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import RotateOverlay from './components/RotateOverlay';
import Stage1 from './stages/Stage1';
import Stage2 from './stages/Stage2';
import Stage3 from './stages/Stage3';
import Stage4 from './stages/Stage4';
import Stage5 from './stages/Stage5';
import Stage6 from './stages/Stage6';
import Stage7 from './stages/Stage7';
import { setBackButtonUrl } from '../../store/uiSlice';
import {
  ASSET,
  fetchProgress,
  parseHMS,
  reportOnStart,
  startGame,
} from '../../utils/sopGame';
import './game.css';

const BG = {
  1: 'bg1.png',
  2: 'bg2.png',
  3: 'bg3.png',
  4: 'bg4.png',
  5: 'bg5.png',
  6: 'bg6.png',
  7: 'bg7.png',
};

const FLIP_MS = 1200;

export default function GamePage() {
  const user = useSelector((s) => s.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [flipping, setFlipping] = useState(false);
  const [turningFrom, setTurningFrom] = useState(null);
  const [flipArmed, setFlipArmed] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [timeeSeconds, setTimeeSeconds] = useState(0);
  const flipTimerRef = useRef(null);

  useEffect(() => {
    dispatch(setBackButtonUrl('/rules'));
  }, [dispatch]);

  useEffect(
    () => () => {
      if (flipTimerRef.current) clearTimeout(flipTimerRef.current);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const started = await startGame(user);
        if (cancelled) return;
        if (started.completed || Number(started.loc) >= 7) {
          navigate('/thank-you', { replace: true });
          return;
        }
        let rid = started.reportId || user?.reportId || null;
        if (started.isNew) {
          const created = await reportOnStart(user, rid);
          if (created) rid = created;
        }
        setReportId(rid);
        setTimeeSeconds(parseHMS(started.timee));
        const startPage = Math.min(7, Math.max(1, (Number(started.loc) || 0) + 1));
        setPage(startPage);
      } catch (err) {
        console.error(err);
        Swal.fire({ text: err.message || 'Failed to start game', icon: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  const nextPage = useCallback(async () => {
    if (flipping) return;
    if (page >= 7) {
      navigate('/thank-you');
      return;
    }
    setFlipping(true);
    const from = page;
    const next = from + 1;

    try {
      const prog = await fetchProgress(user);
      setTimeeSeconds(parseHMS(prog.timee));
      if (prog.reportId) setReportId(prog.reportId);
    } catch {
      /* ignore */
    }

    // Reveal next page underneath, keep current as turning-from (still flat)
    setPage(next);
    setTurningFrom(from);
    setFlipArmed(false);

    // Next frame: arm flip so CSS transition runs
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setFlipArmed(true);
      });
    });

    flipTimerRef.current = setTimeout(() => {
      setTurningFrom(null);
      setFlipArmed(false);
      setFlipping(false);
    }, FLIP_MS);
  }, [flipping, page, navigate, user]);

  const toggleFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.() || el.webkitRequestFullscreen?.();
    } else {
      document.exitFullscreen?.() || document.webkitExitFullscreen?.();
    }
  };

  const stageProps = useMemo(
    () => ({
      user,
      reportId,
      initialSeconds: timeeSeconds,
      onComplete: nextPage,
    }),
    [user, reportId, timeeSeconds, nextPage]
  );

  if (loading) {
    return (
      <div className="sop-game">
        <RotateOverlay />
        <div className="sop-game-loader" />
      </div>
    );
  }

  return (
    <div className="sop-game">
      <RotateOverlay />
      <div className="sop-game-scene">
        <article className="sop-game-book">
          {[1, 2, 3, 4, 5, 6, 7].map((n) => {
            const isTurning = turningFrom === n;
            const isActive = page === n && !isTurning;
            const isFlipped = page > n && !isTurning;
            const showStage = isActive || isTurning;
            return (
              <section
                key={n}
                data-page={n}
                className={[
                  'sop-game-page',
                  isActive ? 'active' : '',
                  isFlipped ? 'flipped' : '',
                  isTurning ? 'turning' : '',
                  isTurning && flipArmed ? 'is-flipping' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div
                  className="sop-game-front"
                  style={{ backgroundImage: `url(${ASSET.img(BG[n])})` }}
                >
                  {showStage ? (
                    <>
                      {n === 1 && <Stage1 {...stageProps} initialSeconds={0} />}
                      {n === 2 && <Stage2 {...stageProps} />}
                      {n === 3 && <Stage3 {...stageProps} />}
                      {n === 4 && <Stage4 {...stageProps} />}
                      {n === 5 && <Stage5 {...stageProps} />}
                      {n === 6 && <Stage6 {...stageProps} />}
                      {n === 7 && <Stage7 {...stageProps} />}
                    </>
                  ) : null}
                </div>
                <div
                  className="sop-game-back"
                  style={{ backgroundImage: `url(${ASSET.img(BG[Math.min(7, n + 1)])})` }}
                  aria-hidden="true"
                />
              </section>
            );
          })}
        </article>
      </div>
      <button type="button" className="sop-fs-btn" onClick={toggleFullscreen}>
        <img src={ASSET.img('fullscreen.png')} alt="Fullscreen" />
      </button>
    </div>
  );
}
